import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import useAuth from "../hooks/useAuth";
import requestErrorHandler from "../util/requestErrorHandler";
import { message } from "antd";
import logoIcon from "../assets/logo-icon.png";
import useSWRMutation from "swr/mutation";
import { handleMutation } from "../util/axiosConfig";
import { Password } from "../components/ui/password";

// Validation schema
const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginForm() {
  const navigate = useNavigate();
  const { logIn, user } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const {trigger, isMutating} = useSWRMutation("/newAdmin/login", handleMutation);

  useEffect(() => {
    if (user) navigate("/");
  }, [user])

  const onSubmit = async (data: LoginFormValues) => {
    try {
      const result = await trigger(data);
      const { userData, token } = result;
      logIn(userData, token);
      message.success("Login successful");
    } catch (error: any) {
      requestErrorHandler(error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <img src={logoIcon} alt="Logo" />
        </div>

        <h2 className="mt-6 text-center text-xl font-semibold text-gray-900">
          Sign in with your work mail.
        </h2>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email address*
              </Label>
              <div className="flex">
                <div className="w-1 bg-blue-600" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  className="flex-1 bg-gray-200 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder-gray-500"
                  disabled={isMutating}
                  {...register("email")}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password*
              </Label>
              <div>
                <div className="w-1 bg-blue-600" />
                <Password
                  id="password"
                  type="password"
                  placeholder="Password"
                  className="w-full flex-1 bg-gray-200 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder-gray-500"
                  disabled={isMutating}
                  {...register("password")}
                />
              </div>
              {errors.password && (
                <p className="text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-none"
          disabled={isMutating || !!errors?.email || !!errors?.password}
          >
            {isMutating ? "Logging in..." : "Continue"}
          </Button>

          {/* <div className="flex items-center justify-center mt-4">
            <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-gray-700">
              Forgot Password
            </Link>
          </div> */}
        </form>
      </div>
    </div>
  );
}
